import type { ReactNode } from "react";

import type { Run } from "@/lib/integrations/api";
import { outcomeCounts, runStatus } from "@/lib/integrations/words";

import { TONE_FILL } from "./HealthPill";
import styles from "./integrations.module.css";

const HEIGHT = 22;

/**
 * The recent runs of one integration as a row of small columns, oldest on the
 * left. Empty slots show how many more runs fit. The words for each run are its
 * label; a failed run is a full red column. `caption` goes under it in words.
 */
export function RunStrip({ runs, slots = 12, caption }: { runs: Run[]; slots?: number; caption?: ReactNode }) {
  const shown = runs.slice(0, slots).reverse();
  const most = Math.max(1, ...shown.map((run) => run.rowsCollected));
  const label = shown.length
    ? `Last ${shown.length} ${shown.length === 1 ? "run" : "runs"}, oldest first: ${shown
        .map((run) => `${runStatus(run.status).word}, ${run.rowsCollected.toLocaleString()} rows`)
        .join("; ")}`
    : "No runs yet";
  const strip = (
    <span className={styles.strip} role="img" aria-label={label} title={label}>
      {Array.from({ length: slots - shown.length }, (_, index) => (
        <span key={`empty-${index}`} className={styles.stripEmpty} />
      ))}
      {shown.map((run) => {
        const tone = runStatus(run.status).tone;
        const parts = outcomeCounts(run.counts);
        const height = run.rowsCollected > 0 ? Math.max(4, Math.round((run.rowsCollected / most) * HEIGHT)) : 4;
        if (tone === "danger") return <span key={run.id} className={`${styles.stripBar} ${styles.stripBad}`} style={{ height: HEIGHT }} />;
        if (parts.length === 0) return <span key={run.id} className={`${styles.stripBar} ${styles.stripQuiet}`} style={{ height }} />;
        return (
          <span key={run.id} className={styles.stripBar} style={{ height }}>
            {parts.map((part) => (
              <span key={part.key} className={TONE_FILL[part.tone]} style={{ flex: `${part.count} 1 0`, minHeight: 1 }} />
            ))}
          </span>
        );
      })}
    </span>
  );
  if (caption === undefined) return strip;
  return (
    <span className={styles.recent}>
      {strip}
      <span className={styles.recentText}>{caption}</span>
    </span>
  );
}
